import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { DEFAULT_BEDS } from '../data/defaultTeamConfigs';

const BedContext = createContext();

export const useBed = () => {
  const context = useContext(BedContext);
  if (!context) {
    throw new Error('useBed must be used within BedProvider');
  }
  return context;
};

export const BedProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastTeamId, setLastTeamId] = useState(null);
  const clearTimeoutRef = useRef(null);
  const currentTeamRef = useRef(currentTeam);

  // Keep currentTeamRef in sync
  useEffect(() => {
    currentTeamRef.current = currentTeam;
  }, [currentTeam]);

  // Fetch beds for current team
  useEffect(() => {
    console.log(`🔍 BedContext - currentTeam changed:`, currentTeam);
    
    // Clear any pending timeout
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    
    if (!currentTeam) {
      // Don't clear beds immediately - wait 1 second in case currentTeam comes back
      // This handles Auth timeout issues where currentTeam temporarily becomes null
      console.log(`⚠️ BedContext - No currentTeam, scheduling delayed clear`);
      
      clearTimeoutRef.current = setTimeout(() => {
        // Re-check currentTeam using ref to avoid stale closure
        if (!currentTeamRef.current) {
          console.log(`⚠️ BedContext - currentTeam still null after delay, clearing beds`);
          setBeds([]);
          setLoading(false);
        } else {
          console.log(`✅ BedContext - currentTeam recovered, keeping beds`);
        }
        clearTimeoutRef.current = null;
      }, 1000); // Wait 1 second before clearing
      
      return;
    }

    // If we have a team, check if it's a different team
    if (lastTeamId && lastTeamId !== currentTeam.id) {
      console.log(`🔄 BedContext - Team changed from ${lastTeamId} to ${currentTeam.id}, clearing old beds`);
      setBeds([]);
    }
    
    setLastTeamId(currentTeam.id);

    console.log(`🔄 BedContext - Starting to fetch beds for team ${currentTeam.id}`);
    setLoading(true);

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'teamBeds'),
        where('teamId', '==', currentTeam.id),
        orderBy('bedNumber', 'asc')
      ),
      (snapshot) => {
        const bedsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`✅ BedContext - Fetched ${bedsData.length} beds for team ${currentTeam.id}`);
        console.log(`📊 BedContext - bedsData:`, bedsData);
        setBeds(bedsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching beds:', err);
        // If index error, use fallback query without orderBy
        if (err.code === 'failed-precondition' || err.message?.includes('index')) {
          console.warn('Index not found, using fallback query without orderBy');
          const fallbackQuery = query(
            collection(db, 'teamBeds'),
            where('teamId', '==', currentTeam.id)
          );
          const fallbackUnsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
            const bedsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            // Sort manually
            bedsData.sort((a, b) => {
              const aNum = parseInt(a.bedNumber) || 0;
              const bNum = parseInt(b.bedNumber) || 0;
              return aNum - bNum;
            });
            console.log(`✅ Fetched ${bedsData.length} beds (fallback) for team ${currentTeam.id}`);
            setBeds(bedsData);
            setLoading(false);
          }, (fallbackErr) => {
            console.error('Error in fallback query:', fallbackErr);
            setError(fallbackErr.message);
            setLoading(false);
          });
          return () => fallbackUnsubscribe();
        } else {
          setError(err.message);
          setLoading(false);
        }
      }
    );

    return () => {
      unsubscribe();
      // Clear timeout on cleanup
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
        clearTimeoutRef.current = null;
      }
    };
  }, [currentTeam, lastTeamId]);

  // Get bed by ID
  const getBedById = (bedId) => {
    return beds.find(bed => bed.id === bedId);
  };

  // Get available beds
  const getAvailableBeds = () => {
    return beds.filter(bed => bed.status === 'AVAILABLE');
  };

  // Get beds by status
  const getBedsByStatus = (status) => {
    return beds.filter(bed => bed.status === status);
  };

  // Get beds by ward
  const getBedsByWard = (ward) => {
    return beds.filter(bed => bed.ward === ward);
  };

  // Add bed
  const addBed = async (bedData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์เพิ่มเตียง');
      }

      // Check if bed number already exists
      const existingBed = beds.find(bed => bed.bedNumber === bedData.bedNumber);
      if (existingBed) {
        throw new Error('หมายเลขเตียงนี้มีอยู่แล้ว');
      }

      const bedRef = await addDoc(collection(db, 'teamBeds'), {
        teamId: currentTeam.id,
        bedNumber: bedData.bedNumber,
        bedName: bedData.bedName,
        bedLocation: bedData.bedLocation || '',
        ward: bedData.ward,
        department: bedData.department,
        floor: bedData.floor || '',
        room: bedData.room || '',
        position: bedData.position || { x: 0, y: 0 },
        status: bedData.status || 'AVAILABLE',
        isDefault: bedData.isDefault || false,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true, bedId: bedRef.id };
    } catch (err) {
      console.error('Error adding bed:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Update bed
  const updateBed = async (bedId, updateData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์แก้ไขเตียง');
      }

      const bedRef = doc(db, 'teamBeds', bedId);
      
      // Check if bed number is being changed and if it conflicts
      if (updateData.bedNumber) {
        const existingBed = beds.find(bed => bed.bedNumber === updateData.bedNumber && bed.id !== bedId);
        if (existingBed) {
          throw new Error('หมายเลขเตียงนี้มีอยู่แล้ว');
        }
      }

      await updateDoc(bedRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error updating bed:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Delete bed
  const deleteBed = async (bedId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ลบเตียง');
      }

      const bedRef = doc(db, 'teamBeds', bedId);
      const bedSnap = await getDoc(bedRef);

      if (!bedSnap.exists()) {
        throw new Error('ไม่พบเตียง');
      }

      const bedData = bedSnap.data();

      // Check if bed is in use
      if (bedData.status === 'IN_USE') {
        throw new Error('ไม่สามารถลบเตียงที่กำลังใช้งานอยู่ได้');
      }

      // Check if it's a default bed
      if (bedData.isDefault) {
        throw new Error('ไม่สามารถลบเตียง default ได้');
      }

      await deleteDoc(bedRef);

      return { success: true };
    } catch (err) {
      console.error('Error deleting bed:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Change bed status
  const changeBedStatus = async (bedId, newStatus) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const bedRef = doc(db, 'teamBeds', bedId);
      await updateDoc(bedRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error changing bed status:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Get equipment using this bed
  const getEquipmentUsingBed = async (bedId) => {
    try {
      if (!currentTeam) return [];

      const equipmentQuery = query(
        collection(db, 'equipment'),
        where('teamId', '==', currentTeam.id),
        where('status', '==', 'IN_USE'),
        where('inUsePatientBed', '==', bedId)
      );

      const snapshot = await getDocs(equipmentQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (err) {
      console.error('Error fetching equipment using bed:', err);
      return [];
    }
  };

  // Create default beds for existing team
  const createDefaultBedsForTeam = async () => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์สร้าง default beds');
      }

      // Check if beds already exist
      const bedsQuery = query(
        collection(db, 'teamBeds'),
        where('teamId', '==', currentTeam.id)
      );
      const bedsSnapshot = await getDocs(bedsQuery);

      if (!bedsSnapshot.empty) {
        throw new Error('ทีมนี้มีเตียงอยู่แล้ว');
      }

      // Create default beds
      const bedsRef = collection(db, 'teamBeds');
      const bedPromises = DEFAULT_BEDS.map((bed) =>
        addDoc(bedsRef, {
          teamId: currentTeam.id,
          bedNumber: bed.bedNumber,
          bedName: bed.bedName,
          bedLocation: '',
          ward: bed.ward,
          department: bed.department,
          floor: '',
          room: '',
          position: { x: 0, y: 0 },
          status: 'AVAILABLE',
          isDefault: true,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      );

      await Promise.all(bedPromises);
      return { success: true, count: DEFAULT_BEDS.length };
    } catch (err) {
      console.error('Error creating default beds:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const value = {
    beds,
    loading,
    error,
    getBedById,
    getAvailableBeds,
    getBedsByStatus,
    getBedsByWard,
    addBed,
    updateBed,
    deleteBed,
    changeBedStatus,
    getEquipmentUsingBed,
    createDefaultBedsForTeam
  };

  return (
    <BedContext.Provider value={value}>
      {children}
    </BedContext.Provider>
  );
};

